// Davis-Bacon certified-payroll dashboard.
//
// Lists the project's REAL weekly certified payrolls — one row per week_ending
// from weekly_payroll_entry (via GET /bead-pm/projects/:id/payroll). Hours and
// KPIs are computed from entered data; there is no synthesized 850-hour constant
// and no hardcoded wage determination. Click a week → the printable Form WH-347
// for that exact week (Print → Save as PDF).
//
// Source: 40 USC 3141 + 29 CFR 5.5.

import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

type PayrollWeek = { week_ending: string; worker_count: number; total_hours: number };

type PayrollResponse = {
  project: {
    id: string; project_code: string; project_name: string;
    county: string | null; state: string | null;
    contract_ref: string | null; wage_determination: string | null;
  };
  weeks: PayrollWeek[];
};

function parseLocalDate(input: any): Date | null {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  const m = String(input).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
}
const fmtDate = (d: any) => {
  const dt = parseLocalDate(d);
  return dt ? dt.toLocaleDateString() : '—';
};

function KpiCard({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) {
  const color =
    tone === 'good' ? '#10b981' :
    tone === 'warn' ? '#f59e0b' :
    tone === 'bad'  ? '#ef4444' : 'var(--text)';
  return (
    <div className="card">
      <div className="card__body" style={{ padding: 16 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
        <div style={{ fontSize: 24, fontWeight: 700, color, marginTop: 4 }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

export default function DavisBaconPage() {
  const [projectId, setProjectId] = useState('');

  const { data: projects = [] } = useQuery<any[]>({
    queryKey: ['bead-projects-davis-bacon'],
    queryFn: () => api.get('/bead/projects'),
  });

  // Auto-select the first real project once the list loads (no stale hardcoded id).
  useEffect(() => {
    if (!projectId && projects.length > 0) setProjectId(projects[0].id);
  }, [projects, projectId]);

  const { data: payroll, isLoading } = useQuery<PayrollResponse>({
    queryKey: ['davis-bacon-payroll', projectId],
    queryFn: () => api.get(`/bead-pm/projects/${projectId}/payroll`),
    enabled: !!projectId,
  });

  const weeks = payroll?.weeks ?? [];
  const project = payroll?.project;

  const totalHours = useMemo(() => weeks.reduce((s, w) => s + (w.total_hours || 0), 0), [weeks]);
  const maxCrews = useMemo(() => weeks.reduce((m, w) => Math.max(m, w.worker_count || 0), 0), [weeks]);

  const wageDeterminationLabel = project?.wage_determination
    ?? (project?.state
        ? `Per subgrant contract wage determination · ${project.county ? project.county + ' County, ' : ''}${project.state}`
        : 'Per subgrant contract wage determination');

  const openWeek = (week: string) => {
    window.open(`/bead-compliance/davis-bacon/wh347/${week}?project=${projectId}`, '_blank');
  };

  return (
    <>
      <div className="page__header">
        <div>
          <h1 className="page__title">Davis-Bacon</h1>
          <div className="page__subtitle">
            Weekly Form WH-347 certified payrolls · 29 CFR 5.5 · {wageDeterminationLabel}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={projectId} onChange={e => setProjectId(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border)', fontSize: 13, minWidth: 220 }}>
            {projects.map((p: any) => (
              <option key={p.id} value={p.id}>{p.project_code} — {p.project_name}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <KpiCard label="Weeks recorded" value={`${weeks.length}`} sub="Certified payrolls on file" />
        <KpiCard label="Peak crew size" value={`${maxCrews}`} sub="Workers in a single week" />
        <KpiCard label="Total labor hours" value={totalHours.toLocaleString()} tone="good" sub="Across recorded weeks" />
        <KpiCard label="State" value={project?.state ?? '—'} sub={project?.county ? `${project.county} County` : 'Wage determination region'} />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card__body" style={{ padding: 14, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ fontSize: 22, lineHeight: 1, padding: '4px 8px', borderRadius: 6, background: 'rgba(245,158,11,0.12)', color: '#d97706' }}>★</div>
          <div style={{ flex: 1, fontSize: 13, color: '#475569', lineHeight: 1.55 }}>
            <b style={{ color: '#0f172a' }}>How this works.</b> Each row below is one week's certified
            payroll, built from the crew hours &amp; wages entered for this project. Click any row to
            generate the printable Form WH-347 (with Statement of Compliance) for signature and
            submission to the SBO. The form is populated with worker classifications, hours, gross
            wages, and deductions from the entered payroll — never placeholder data.
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card__header">
          <span>Weekly Certified Payrolls</span>
        </div>
        <div className="card__body" style={{ padding: 0 }}>
          {isLoading ? (
            <div style={{ padding: 16, color: 'var(--text-muted)' }}>Loading payrolls…</div>
          ) : weeks.length === 0 ? (
            <div style={{ padding: 24, color: 'var(--text-muted)', lineHeight: 1.6 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
                No certified payroll recorded
              </div>
              No weekly payroll entries exist for this project yet. Add weekly payroll
              (crew hours &amp; wages) to begin generating Form WH-347 certified payrolls.
              A signable WH-347 is only produced from entered payroll data.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                  <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>Week ending</th>
                  <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>Payroll no.</th>
                  <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>Workers</th>
                  <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', textAlign: 'right' }}>Hours</th>
                  <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {weeks.map((w) => {
                  // Payroll No. = 1-based rank ascending (oldest week = 001).
                  const asc = weeks.map(x => x.week_ending).slice().sort();
                  const payrollNo = asc.indexOf(w.week_ending) + 1;
                  return (
                    <tr key={w.week_ending}
                      onClick={() => openWeek(w.week_ending)}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
                      style={{ borderTop: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.12s' }}>
                      <td style={{ padding: '10px 16px', fontWeight: 600 }}>{fmtDate(w.week_ending)}</td>
                      <td style={{ padding: '10px 16px', color: 'var(--text-muted)' }}>{String(payrollNo).padStart(3, '0')}</td>
                      <td style={{ padding: '10px 16px' }}>{w.worker_count}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{(w.total_hours || 0).toLocaleString()}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#3b82f6' }}>Open WH-347 →</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
